import { renderToStaticMarkup } from "react-dom/server";
import { STDoc } from "../engine/stationery-doc";
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { getErrorMessage, getToken } from '@/utils/common';
import { getV1TemplatesId, patchV1TemplatesId, getV1SubjectsId, getV1FormBuilderRelationalSchemaFieldsName, getV1Templates, patchV1TemplatesIdSetDefault, getV1TemplateHeaderFooterCompanyIdHeaderFooter } from '@/api-client/api.system-feature/api';
import TemplateNameModal from '../template-name-modal/template-name-modal';
// V2 Engine Imports
import { Ic } from '../engine/icons';
import { PBData } from '../engine/schema';
import { PBPaper } from '../engine/paginator';
import { PBEdit } from '../engine/editor';
import { PBControls } from '../engine/controls';
import { PBModel } from '../engine/model';
import { STStore } from '../engine/stationery-store';
import { PBTemplates } from '../engine/templates';
import { PBTpl } from '../engine/templatebuilder';
import { PBPicker } from '../engine/picker';
import { PBEngine } from '../engine/engine';
import { StationeryMasterApp } from '../engine/stationery-app';
import { compileV2TemplateToV1Payload } from '../utils/compiler';
import { DndContext, DragEndEvent, DragOverEvent, pointerWithin, closestCenter, DragOverlay, DragStartEvent, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import EditorCanvas from './EditorCanvas';
import { generateSectionsHTML, initialStyles, cssScopingService, getInitialSectionData, generateEmailPreview, generateContentHTML } from '../utils/common';
import { STATIONERY_CSS } from '../utils/constant';
import { ElementType, EnhancedSectionData } from '../../types/editor';

// V2 Styles
// Removed V2 index.scss import
import '../engine/styles.css';
import '../engine/doc.css';
import '../engine/stationery.css';
import '../engine/letterpads.css';


const { DOCS, order } = PBData;
const variantsOf = (fam: string) => fam ? order.filter(id => DOCS[id].family === fam) : [];
const { PaperDoc, dims } = PBPaper;
const { CodeEditor, FieldTree } = PBEdit;
const { ControlsPanel } = PBControls;

const BASE_COMPANY = DOCS.quotation.sample.company;

const defStyle = (id: string) => ({
  accent: DOCS[id].docAccent,
  density: 'normal',
  headWeight: 700,
  showLogo: true,
  logoSize: 24,
  headStyle: 'standard',
  watermark: {
    show: false,
    opacity: 15,
    size: 50,
    rotation: 0,
    image: '',
  },
});
const defFrame = () => ({
  header: { show: true, repeat: 'all', height: 34, showLogo: true, left: 'company', right: 'docref' },
  footer: { show: true, repeat: 'all', height: 34, pageNumbers: true, format: 'Page {n} of {total}' },
});
const defFlags = (id: string) => {
  const f: any = { showFooter: true };
  (DOCS[id].includes || []).forEach((i: any) => { f[i.flag] = i.flag === 'showTerms' ? (DOCS[id].sample.terms || []).length > 0 : true; });
  return f;
};

// ═══════════════ stationery picker (toolbar) ═══════════════
function StationeryPicker({ lib, value, onChange, onManage }: any) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: any) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e: any) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  const cur = value === '__blank__' ? { name: 'Blank', icon: 'file', tone: null }
    : value === '__preprinted__' ? { name: 'Pre-printed', icon: 'file', tone: null }
      : (() => { const p = STStore.getById(lib, value) || lib.presets[0] || {}; return { name: p.name || 'Stationery', icon: 'layers', tone: p.brand }; })();
  const pick = (v: string) => { onChange(v); setOpen(false); };

  return (
    <div className="pb-stnpick" ref={ref}>
      <button className={`pb-stnpick-btn ${open ? 'is-open' : ''}`} onClick={() => setOpen(o => !o)} title="Letterhead & footer for this document">
        {cur.tone ? <span className="pb-stnpick-dot" style={{ background: cur.tone }} /> : <Ic k={cur.icon} />}
        <span className="pb-stnpick-lbl">{cur.name}</span>
        <Ic k="chevD" />
      </button>
      {open && (
        <div className="pb-stnpick-menu">
          <div className="pb-stnpick-grp">Stationery library</div>
          {lib.presets.map((p: any) => (
            <button key={p.id} className={`pb-stnpick-item ${value === p.id ? 'is-sel' : ''}`} onClick={() => pick(p.id)}>
              <span className="pb-stnpick-dot" style={{ background: p.brand }} />
              <span className="pb-stnpick-itemt">{p.name}<small>{p.mode === 'preprinted' ? 'Pre-printed margins' : `${p.header?.variant} header · ${p.footer?.regBand ? 'reg band' : 'slim'} footer`}</small></span>
              {value === p.id && <Ic k="check" />}
            </button>
          ))}
          <div className="pb-stnpick-grp">No letterhead</div>
          <button className={`pb-stnpick-item ${value === '__blank__' ? 'is-sel' : ''}`} onClick={() => pick('__blank__')}>
            <span className="pb-stnpick-ic"><Ic k="file" /></span>
            <span className="pb-stnpick-itemt">Blank<small>Nothing — bare document</small></span>
            {value === '__blank__' && <Ic k="check" />}
          </button>
          <button className={`pb-stnpick-item ${value === '__preprinted__' ? 'is-sel' : ''}`} onClick={() => pick('__preprinted__')}>
            <span className="pb-stnpick-ic"><Ic k="file" /></span>
            <span className="pb-stnpick-itemt">Pre-printed paper<small>Reserve top &amp; bottom margins</small></span>
            {value === '__preprinted__' && <Ic k="check" />}
          </button>

        </div>
      )}
    </div>
  );
}

// ═══════════════ template-variant picker (set as primary) ═══
function VariantPicker({ subjectId, templateId, companyId, currentName, docId, onNavigate }: any) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  console.log('VariantPicker render, templates:', templates);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let isMounted = true;
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        let filters = `(subject_id.eq=${subjectId})`;
        if (companyId) filters = `(company_id.eq=${companyId}&subject_id.eq=${subjectId})`;

        let res = await getV1Templates({ ...getToken(), filters });
        let data = Array.isArray(res) ? res : (res?.data || []);
        if (data && !Array.isArray(data) && Array.isArray(data.data)) data = data.data;

        // Fallback: If 0 results, fetch without companyId
        if (isMounted && Array.isArray(data) && data.length === 0) {
          let fbRes = await getV1Templates({ ...getToken(), filters: `(subject_id.eq=${subjectId})` });
          let fbData = Array.isArray(fbRes) ? fbRes : (fbRes?.data || []);
          if (fbData && !Array.isArray(fbData) && Array.isArray(fbData.data)) fbData = fbData.data;
          if (Array.isArray(fbData) && fbData.length > 0) data = fbData;
        }

        if (isMounted && Array.isArray(data)) {
          setTemplates(data);
        }
      } catch (e) { console.error(e); }
      if (isMounted) setLoading(false);
    };
    fetchTemplates();

    const onDoc = (e: any) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e: any) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onEsc);
    return () => {
      isMounted = false;
      document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc);
    };
  }, [open, subjectId, companyId]);

  const handleSetPrimary = async (e: any, id: string) => {
    e.stopPropagation();
    try {
      await patchV1TemplatesIdSetDefault({ ...getToken(), id });
      enqueueSnackbar('Template set as primary', { variant: 'success' });
      // update local state to reflect the new primary instantly
      setTemplates(prev => prev.map(t => ({ ...t, is_default: t._id === id })));
    } catch (err: any) {
      enqueueSnackbar(getErrorMessage(err?.message), { variant: 'error' });
    }
  };

  const primaryTemplate = templates.find(t => t.is_default);
  const isCurrentPrimary = templates.length ? !!templates.find(t => t._id === templateId && t.is_default) : false;

  return (
    <div className="pb-varpick" ref={ref}>
      <button className={`pb-stnpick-btn ${open ? 'is-open' : ''}`} onClick={() => setOpen(o => !o)} title="Switch templates or set primary">
        <span className="pb-varpick-dot" style={{ background: DOCS[docId]?.docAccent || '#1B2C6E' }} />
        <span className="pb-stnpick-lbl">{currentName || 'Template'}</span>
        {isCurrentPrimary && <span className="pb-varpick-star" title="Primary template"><Ic k="check" /></span>}
        <Ic k="chevD" />
      </button>
      {open && (
        <div className="pb-stnpick-menu pb-varpick-menu">
          <div className="pb-stnpick-grp">Available Templates</div>
          {loading ? (
            <div style={{ padding: '8px 16px', fontSize: 13, color: '#666' }}>Loading templates...</div>
          ) : templates.map(t => (
            <button key={t._id} className={`pb-stnpick-item ${t._id === templateId ? 'is-sel' : ''}`} onClick={() => { setOpen(false); if (t._id !== templateId) onNavigate(t._id); }}>
              <span className="pb-stnpick-itemt">{t.name || 'Untitled'}</span>
              {t._id === templateId && <Ic k="check" />}
            </button>
          ))}

        </div>
      )}
    </div>
  );
}

// ═══════════════ V2 SectionFlowEditor (replaces Builder) ═══════════════════════════════════
const SectionFlowEditor: React.FC<{ modulePath: string; module: string; type: string }> = ({ modulePath, module, type }) => {
  const navigate = useNavigate();
  const { id, template_id } = useParams();
  const locationState = useLocation().state as any;
  const selectedCompany = useSelector((state: any) => state?.company?.selectedCompany?._id);
  const [viewMode, setViewMode] = useState<'template' | 'stationery'>('template');
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const tT = useRef<any>(null);

  const flash = (msg: string) => { setToastMsg(msg); clearTimeout(tT.current); tT.current = setTimeout(() => setToastMsg(null), 2000); };

  const [apiPayload, setApiPayload] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState<any>(null);
  // Using a hardcoded docId for Phase 1 as this redesign maps to the V2 doc concepts. 
  // In Phase 2, this will bridge with the backend `template_id`.
  const [docId, setDocId] = useState('invoice');

  const doc = DOCS[docId];
  const [stLib, setStLib] = useState(() => STStore.load());
  const stDefault = docId === 'invoice_ksa'
    ? '__blank__'
    : ((stLib.presets[0] && stLib.presets[0].id) || '__blank__');

  const [brand, setBrand] = useState({ company: { ...BASE_COMPANY }, footerNote: 'This is a system-generated document. ' + BASE_COMPANY.web });
  const [primaries, setPrimaries] = useState<any>({});

  const [name, setName] = useState(doc.full);
  const [template, setTemplate] = useState('');
  const [docData, setDocData] = useState(() => PBModel.recomputeTotals(PBModel.clone(doc.sample)));
  const [style, setStyle] = useState(defStyle(docId));
  const [frame, setFrame] = useState(defFrame());
  const [flags, setFlags] = useState(defFlags(docId));
  const [paper, setPaper] = useState('A4');
  const [orient, setOrient] = useState('portrait');
  const [stationerySel, setStationerySel] = useState(stDefault);
  const [savedJSON, setSavedJSON] = useState<string | null>('');

  const [dock, setDock] = useState('none');
  const [sideOpen, setSideOpen] = useState(true);
  const [railTab, setRailTab] = useState('fields');
  const [zoom, setZoom] = useState<any>('fit');
  const [fitScale, setFitScale] = useState(0.8);
  const [pageCount, setPageCount] = useState(1);
  const [showHint, setShowHint] = useState(true);
  const isInvoice = docId === 'invoice';
  const [cols, setCols] = useState(() => PBTpl.defaultColumns());
  const [metaFields, setMetaFields] = useState(() => PBTpl.defaultMeta());
  const [lens, setLens] = useState('placeholder');
  const [customTpl, setCustomTpl] = useState<any>('');
  const [dockW, setDockW] = useState(600);

  const editorApi = useRef<any>({});
  const ctxRef = useRef<any>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const unitsRef = useRef<any>([]);
  const [picker, setPicker] = useState<any>(null);

  const [dynamicGroups, setDynamicGroups] = useState<any[]>([]);

  const [overSection, setOverSection] = useState<string | null>(null);
  const [activeCanvas, setActiveCanvas] = useState<string>('main');
  const [activeCanvasSection, setActiveCanvasSection] = useState<any>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [activeDragData, setActiveDragData] = useState<any>(null);
  const [debugError, setDebugError] = useState<any>('');

  // ── Drag and Drop Canvas State ──
  // ── Multi-page canvas state ──
  // Each page: { id: string; elements: EnhancedSectionData[] }
  const [pages, setPages] = useState<{ id: string; elements: EnhancedSectionData[] }[]>([{ id: 'page-1', elements: [] }]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  // Legacy flat sections — derived from current page for backward compat with sub-components that still use `sections`
  const sections = pages[currentPageIndex]?.elements ?? [];
  const setSections = useCallback((updater: EnhancedSectionData[] | ((prev: EnhancedSectionData[]) => EnhancedSectionData[])) => {
    setPages(prev => {
      const next = [...prev];
      const idx = currentPageIndex;
      if (!next[idx]) return prev;
      const newElems = typeof updater === 'function' ? updater(next[idx].elements) : updater;
      next[idx] = { ...next[idx], elements: newElems };
      return next;
    });
  }, [currentPageIndex]);

  const addPage = useCallback(() => {
    setPages(prev => [...prev, { id: `page-${Date.now()}`, elements: [] }]);
    setCurrentPageIndex(prev => prev + 1);
  }, []);

  const deletePage = useCallback((idx: number) => {
    if (pages.length <= 1) return; // must keep at least 1 page
    setPages(prev => prev.filter((_, i) => i !== idx));
    setCurrentPageIndex(prev => Math.max(0, prev >= idx ? prev - 1 : prev));
  }, [pages.length]);

  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [activeDragType, setActiveDragType] = useState<string | null>(null);

  const fetchSchema = useCallback(async () => {
    try {
      if (!id) {
        setDebugError('No ID in params');
        return;
      }
      const token = getToken();
      const subjRes = await getV1SubjectsId({ ...token, id });

      if (subjRes.data && (subjRes.data as any).resource) {
        const resource = (subjRes.data as any).resource;
        const fRes = await getV1FormBuilderRelationalSchemaFieldsName({ ...token, name: resource });

        if (!fRes || !fRes.data) {
          setDebugError('fRes no data: ' + JSON.stringify(fRes));
          return;
        }

        const rawFields = ((fRes.data as any)?.fields || []);
        const relTables = ((fRes.data as any)?.relational_fields || []);

        const mainFields = rawFields
          .map((f: any) => ({ ...f, key: (f.key || '').replace('__replaceKey', '') }))
          .filter((f: any) => !f.is_inner_join && f.alias && f.alias !== 'id' && !f.alias.includes('_id'));

        let dynamicCatalog: any = {
          "doc": {
            key: "doc",
            icon: "doc",
            label: "Document Info",
            fields: mainFields.map((f: any) => ({
              label: f.alias.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
              tag: `{{${f.alias}}}`
            }))
          }
        };

        relTables.forEach((table: any) => {
          if (!table) return;
          const tableFields = (table.fields || [])
            .map((f: any) => ({ ...f, key: (f.key || '').replace('__replaceKey', ''), alias: f.templateKey || f.alias }))
            .filter((f: any) => !f.is_inner_join && f.alias && f.alias !== 'id' && !f.alias.includes('_id'));

          const uniqueTableFields = Array.from(new Map(tableFields.map((item: any) => [item.alias, item])).values());

          if (table.type === 'object') {
            const groupName = table.dataKey ? table.dataKey.replace(/_data$/, '') : 'Related';
            dynamicCatalog[table.dataKey] = {
              key: table.dataKey,
              icon: 'link',
              label: groupName.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
              fields: uniqueTableFields.map((f: any) => ({
                label: (f.alias || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
                tag: `{{${table.dataKey}.${f.alias}}}`
              }))
            };
          } else if (table.type === 'array') {
            const groupName = table.dataKey ? table.dataKey : 'Items';
            dynamicCatalog[table.dataKey] = {
              key: table.dataKey,
              icon: 'list',
              label: groupName.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
              fields: uniqueTableFields.map((f: any) => ({
                label: (f.alias || '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
                tag: `{{${table.dataKey}.[].${f.alias}}}`
              }))
            };
          }
        });

        setDynamicGroups(Object.values(dynamicCatalog));
      } else {
        setDebugError('subjRes no resource: ' + JSON.stringify(subjRes));
      }
    } catch (e) {
      console.error('Failed to fetch schema', e);
      setDebugError('Catch error: ' + (e as any).message);
    }
  }, [id]);

  useEffect(() => { fetchSchema(); }, [fetchSchema]);

  const fieldCatalog = useMemo(() => PBPicker.buildFieldCatalog(dynamicGroups && dynamicGroups.length > 0 ? dynamicGroups : doc.groups), [doc, dynamicGroups]);
  const labelFor = useCallback((helper: string, path: string) => PBPicker.fieldLabel(fieldCatalog, helper, path), [fieldCatalog]);

  const data = useMemo(() => ({
    ...docData, company: brand.company, footerNote: brand.footerNote,
    showLogo: style.showLogo, ...flags,
  }), [docData, brand, style.showLogo, flags]);

  const itemsGroup = dynamicGroups?.find((g: any) => g.key !== 'doc' && (g.icon === 'list' || g.key?.includes('item')));
  const arrayKey = itemsGroup ? itemsGroup.key : 'items';

  const generatedTemplate = useMemo(() => (
    isInvoice && template ? PBTpl.composeInvoice(PBTemplates.invoice, cols, metaFields, arrayKey) : template
  ), [isInvoice, template, cols, metaFields, arrayKey]);
  const isCustom = customTpl != null && customTpl !== false;
  const effTemplate = isCustom ? customTpl : generatedTemplate;

  const stSel = useMemo(() => {
    if (stationerySel === '__blank__') return { kind: 'blank', preset: null };
    if (stationerySel === '__preprinted__') return { kind: 'preprinted', preset: { reserve: { top: 150, bottom: 110 } } };
    const p = STStore.getById(stLib, stationerySel) || stLib.presets[0] || null;
    if (!p) return { kind: 'blank', preset: null };
    if (p.mode === 'preprinted') return { kind: 'preprinted', preset: p };
    return { kind: 'preset', preset: p };
  }, [stationerySel, stLib]);


  const stationeryHeaderHtml = useMemo(() => {
    if (stSel?.kind === 'preset' && stSel.preset?.header?.show) {
      const effPreset = { ...stSel.preset, paper: paper || stSel.preset.paper || 'A4', orient: orient || stSel.preset.orient || 'portrait' };
      return STATIONERY_CSS + renderToStaticMarkup(<STDoc.Header m={effPreset} compact={false} ed={null} />);
    }
    if (stSel?.kind === 'preprinted' && stSel.preset) {
      return STATIONERY_CSS + `<div class="st-reserve st-reserve--top" style="height: ${stSel.preset.reserve?.top || 150}px"></div>`;
    }
    return '';
  }, [stSel, paper, orient]);

  const stationeryFooterHtml = useMemo(() => {
    if (stSel?.kind === 'preset' && stSel.preset?.footer?.show) {
      const effPreset = { ...stSel.preset, paper: paper || stSel.preset.paper || 'A4', orient: orient || stSel.preset.orient || 'portrait' };
      return STATIONERY_CSS + renderToStaticMarkup(<STDoc.Footer m={effPreset} pageIndex={0} total={1} compact={false} ed={null} />);
    }
    if (stSel?.kind === 'preprinted' && stSel.preset) {
      return STATIONERY_CSS + `<div class="st-reserve st-reserve--bot" style="height: ${stSel.preset.reserve?.bottom || 110}px"></div>`;
    }
    return '';
  }, [stSel, paper, orient]);

  const payload = useMemo(() => ({
    type: 'canvas',
    docId, docData, template, cols, metaFields, customTpl, paper, orient, style: { ...style, paper, orient }, frame, flags, name, stationerySel, stConfig: stSel,
    // Multi-page: store pages[] in payload. sections kept for backward compat.
    pages,
    sections: pages[currentPageIndex]?.elements ?? []
  }), [docId, docData, template, cols, metaFields, customTpl, style, paper, orient, frame, flags, name, stationerySel, stSel, pages, currentPageIndex]);

  // Default sizes for each element type when dropped onto the free canvas
  const DEFAULT_ELEMENT_SIZES: Record<string, { width: number; height: number }> = {
    text: { width: 300, height: 60 },
    'text-block': { width: 300, height: 80 },
    heading: { width: 360, height: 60 },
    'heading-2': { width: 320, height: 50 },
    'heading-3': { width: 280, height: 45 },
    'heading-4': { width: 260, height: 40 },
    image: { width: 200, height: 150 },
    buttons: { width: 130, height: 40 },
    divider: { width: 400, height: 20 },
    table: { width: 500, height: 78 },
    signature: { width: 200, height: 60 },
    'qr-code': { width: 100, height: 100 },
    'bar-code': { width: 180, height: 80 },
  };

  // For Phase 1 we use an internal dirty flag based on JSON comparison
  const dirty = savedJSON != null && JSON.stringify(payload) !== savedJSON;

  const compiled = useMemo(() => {
    if (lens === 'placeholder') {
      const r = PBTpl.toEditableTemplateView(effTemplate, data, labelFor);
      unitsRef.current = r.units;
      return r.html;
    }
    return PBEngine.render(effTemplate, data).html;
  }, [effTemplate, data, lens]);

  ctxRef.current = { docId, template: effTemplate, style, docAccent: doc.docAccent };

  const applyMaster = useCallback(async () => {
    try {
      setIsDataLoading(true);
      if (template_id) {
        const token = getToken();
        const res = await getV1TemplatesId({ ...token, id: template_id });
        if (res.status_code === 200 && res.data) {
          const data: any = res.data;
          setApiPayload(data);
          let v2StateObj: any = null;

          // 1. Check for Base64 encoded state in the HTML (New standard)
          if (data.contents) {
            const match = data.contents.match(/<meta name="v2-state" content="([^"]+)">/);
            if (match && match[1]) {
              try {
                v2StateObj = JSON.parse(decodeURIComponent(escape(atob(match[1]))));
              } catch (e) {
                console.error('Failed to parse V2 state from HTML meta tag', e);
              }
            }
          }

          // 2. Fallback to old section-based state (if saved before the Base64 fix)
          if (!v2StateObj) {
            const v2Section = (data.sections || []).find((s: any) => s.type === 'v2-state');
            if (v2Section && v2Section.content) {
              try {
                const decodedStr = decodeURIComponent(escape(atob(v2Section.content)));
                v2StateObj = JSON.parse(decodedStr);
              } catch (e) {
                try {
                  v2StateObj = JSON.parse(v2Section.content);
                } catch (e2) {
                  console.error('Failed to parse V2 state from sections', e2);
                }
              }
            }
          }

          if (v2StateObj) {
            // New block-based state
            if (v2StateObj.paper && !v2StateObj.style) {
              setPaper(v2StateObj.paper);
            }
            if (v2StateObj.settings) {
              // setSettings is not defined; ignore legacy settings field gracefully
            }

            // Legacy state (if any)
            if (v2StateObj.docId) setDocId(v2StateObj.docId);
            if (v2StateObj.docData) setDocData(v2StateObj.docData);
            if (v2StateObj.template) setTemplate(v2StateObj.template);
            if (v2StateObj.cols) setCols(v2StateObj.cols);
            if (v2StateObj.metaFields) setMetaFields(v2StateObj.metaFields);
            if (v2StateObj.customTpl) setCustomTpl(v2StateObj.customTpl);
            if (v2StateObj.style) setStyle({ ...defStyle(docId), ...v2StateObj.style, watermark: { ...defStyle(docId).watermark, ...(v2StateObj.style.watermark || {}) } });
            if (v2StateObj.frame) setFrame(v2StateObj.frame);
            if (v2StateObj.flags) setFlags(v2StateObj.flags);
            if (v2StateObj.name) setName(v2StateObj.name);
            if (v2StateObj.stationerySel) setStationerySel(v2StateObj.stationerySel);

            // Legacy paper state from style
            if (v2StateObj.style?.paper) setPaper(v2StateObj.style.paper);
            if (v2StateObj.style?.orient) setOrient(v2StateObj.style.orient);
            // Multi-page: restore pages[] if present, otherwise migrate flat sections[] to single page
            const migrateSections = (raw: any[]): EnhancedSectionData[] =>
              raw.map((s: any, i: number) => {
                let content = s.content;
                if (['image', 'divider', 'table', 'buttons', 'signature', 'qr-code', 'bar-code'].includes(s.type) && !content) {
                  content = generateContentHTML(s.type, '', s.attributes || {}, []);
                }
                return {
                  ...s, content,
                  x: s.x ?? 52, y: s.y ?? (52 + i * 90),
                  width: s.width ?? 500, height: s.height ?? 80,
                  rotation: s.rotation ?? 0, zIndex: s.zIndex ?? (i + 1),
                } as EnhancedSectionData;
              });

            if (v2StateObj.pages && Array.isArray(v2StateObj.pages) && v2StateObj.pages.length > 0) {
              // New multi-page format
              const restoredPages = v2StateObj.pages.map((p: any) => ({
                id: p.id || `page-${Date.now()}`,
                elements: migrateSections(p.elements || [])
              }));
              setPages(restoredPages);
              setCurrentPageIndex(0);
            } else {
              // Legacy flat sections[] — wrap in single page
              const rawSections: any[] = v2StateObj.sections || [];
              setPages([{ id: 'page-1', elements: migrateSections(rawSections) }]);
              setCurrentPageIndex(0);
            }
            setSavedJSON(JSON.stringify(v2StateObj));
            return;
          } else if (data.contents) {
            // Legacy template fallback: load raw HTML into customTpl so it is preserved
            const blankDocData = PBModel.clone(doc.sample);
            blankDocData.items = [];
            const next = {
              docId,
              docData: PBModel.recomputeTotals(blankDocData),
              template: PBTemplates[docId],
              cols: PBTpl.defaultColumns(),
              metaFields: PBTpl.defaultMeta(),
              customTpl: data.contents,
              style: defStyle(docId),
              frame: defFrame(),
              flags: defFlags(docId),
              name: data.subject?.subject || data.name || doc.full,
              stationerySel: '__blank__',
              stConfig: { kind: 'blank', preset: null },
              sections: data.sections && data.sections.length > 0 ? data.sections : []
            };
            setDocData(next.docData); setTemplate(next.template); setCols(next.cols);
            setMetaFields(next.metaFields); setCustomTpl(next.customTpl); setStyle(next.style);
            setFrame(next.frame); setFlags(next.flags); setName(next.name); setStationerySel(next.stationerySel);

            // Migrate V1 sections to multi-page (wrap all in page 1)
            if (data.sections && data.sections.length > 0) {
              const migratedSections = data.sections.map((s: any, i: number) => {
                let content = s.content;
                if (['image', 'divider', 'table', 'buttons', 'signature', 'qr-code', 'bar-code'].includes(s.type) && !content) {
                  content = generateContentHTML(s.type, '', s.attributes || {}, []);
                }
                if (s.x !== undefined && s.y !== undefined) return { ...s, content } as EnhancedSectionData;
                return {
                  ...s, content,
                  x: s.x ?? 50, y: s.y ?? (50 + i * 100),
                  width: s.width ?? Math.min(700, 400 + ((content || '').length > 100 ? 300 : 0)),
                  height: s.height ?? 100,
                  rotation: s.rotation ?? 0, zIndex: s.zIndex ?? (i + 1),
                } as EnhancedSectionData;
              });
              setPages([{ id: 'page-1', elements: migratedSections }]);
            } else {
              setPages([{ id: 'page-1', elements: [] }]);
            }
            setCurrentPageIndex(0);

            setSavedJSON(JSON.stringify(next));
            return;
          } else {
            setName(data.subject?.subject || data.name || doc.full);
          }
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsDataLoading(false);
    }

    // Fallback to defaults if no template_id or missing v2-state (New Template)
    const blankSample = PBModel.clone(doc.sample);
    blankSample.items = []; // Start with no sample rows to look blank

    const next = {
      docData: PBModel.recomputeTotals(blankSample),
      template: '',
      cols: PBTpl.defaultColumns(),
      metaFields: PBTpl.defaultMeta(),
      customTpl: '',
      style: defStyle(docId), frame: defFrame(), flags: defFlags(docId), name: doc.full,
      stationerySel: '__blank__',
      stConfig: { kind: 'blank', preset: null },
      pages: [{ id: 'page-1', elements: [] }]
    };
    setDocData(next.docData); setTemplate(next.template); setCols(next.cols);
    setMetaFields(next.metaFields); setCustomTpl(next.customTpl); setStyle(next.style);
    setFrame(next.frame); setFlags(next.flags); setName(next.name); setStationerySel(next.stationerySel);
    setPaper('A4'); setOrient('portrait');
    setPages([{ id: 'page-1', elements: [] }]);
    setCurrentPageIndex(0);
    setSavedJSON(JSON.stringify(next));
  }, [docId, doc, stDefault, template_id]);

  useEffect(() => { applyMaster(); }, [applyMaster]);

  useEffect(() => {
    const fetchStationery = async () => {
      const companyId = locationState?.company_id || selectedCompany;
      if (!companyId) return;
      try {
        const token = getToken();
        const res = await getV1TemplateHeaderFooterCompanyIdHeaderFooter({ ...token, company_id: Number(companyId) } as any);
        if ((res.status_code === 200 || res.success) && res.data) {
          const hfData = Array.isArray(res.data) ? res.data[0] : res.data;
          if (!hfData) return;
          const v2Section = (hfData.header_sections || []).find((s: any) => s.attributes?.stationeryV2Config || s.type === 'stationery-v2');
          if (v2Section) {
            try {
              let loadedLib;
              if (v2Section.attributes?.stationeryV2Config) {
                loadedLib = typeof v2Section.attributes.stationeryV2Config === 'string' ? JSON.parse(v2Section.attributes.stationeryV2Config) : v2Section.attributes.stationeryV2Config;
              } else if (v2Section.content) {
                loadedLib = JSON.parse(v2Section.content);
              }
              if (loadedLib) setStLib(loadedLib);
            } catch (e) {
              console.error('Failed to parse stationery-v2 content', e);
            }
          }
        }
      } catch (e) {
        console.error('Failed to fetch stationery library', e);
      }
    };
    fetchStationery();
  }, [locationState?.company_id, selectedCompany]);

  const toast = flash;
  const setDirty = (val: boolean) => { /* Derived from savedJSON */ };

  const saveMaster = useCallback(async () => {
    try {
      const v2Payload = payload;
      const compiledData = compileV2TemplateToV1Payload(v2Payload, effTemplate);

      const p = {
        ...getToken(),
        ...(template_id ? { id: template_id } : { subject_id: id, is_default: false, company_id: locationState?.company_id ? Number(locationState.company_id) : undefined }),
        sections: compiledData.sections,
        contents: compiledData.contents
      };

      if (template_id) {
        setIsDataLoading(true);
        p.name = name;
        const res = await patchV1TemplatesId(p);
        setIsDataLoading(false);
        if (res?.status_code === 200 || res?.status_code === 201 || res?.success) {
          toast('Template saved successfully');
          setSavedJSON(JSON.stringify(v2Payload)); // Update dirty state
        } else {
          enqueueSnackbar(res?.message || 'Failed to save template', { variant: 'error' });
        }
      } else {
        setApiPayload(p);
        setIsModalOpen(true);
      }
    } catch (e) {
      setIsDataLoading(false);
      console.error(e);
      enqueueSnackbar('Failed to prepare save payload', { variant: 'error' });
    }
  }, [payload, docId, docData, template, cols, metaFields, customTpl, style, paper, orient, frame, flags, name, stationerySel, stSel, effTemplate, data, template_id, id, locationState, pages, currentPageIndex]);

  const handleSaveSuccess = () => {
    setSavedJSON(JSON.stringify(payload));
    setIsModalOpen(false);
    setApiPayload(null);
    toast('Template saved successfully');
  };

  const handleSaveError = () => {
    toast('Failed to save template');
  };

  const discardChanges = useCallback(() => { applyMaster(); toast('Reverted to last saved master'); }, [applyMaster]);

  useEffect(() => {
    let el = document.getElementById('pb-page-style');
    if (!el) { el = document.createElement('style'); el.id = 'pb-page-style'; document.head.appendChild(el); }
    el.textContent = `@page { size: ${paper} ${orient}; margin: 0; }`;
  }, [paper, orient]);

  useEffect(() => {
    const el = scrollRef.current; if (!el) return;
    const calc = () => { const avail = el.clientWidth - 56; setFitScale(Math.max(0.3, Math.min(1, avail / dims(paper, orient).w))); };
    calc();
    const ro = new ResizeObserver(calc); ro.observe(el); return () => ro.disconnect();
  }, [dock, sideOpen, paper, orient, viewMode]);

  const z = zoom === 'fit' ? fitScale : zoom;

  const onEdit = useCallback((path: string, type: string, text: any) => {
    if (path === 'footerNote') { setBrand(b => PBModel.applyEdit(b, 'footerNote', type, text)); return; }
    if (path.indexOf('company.') === 0) { setBrand(b => PBModel.applyEdit(b, path, type, text)); return; }
    setDocData(d => PBModel.applyEdit(d, path, type, text));
  }, []);

  const itemOps = useMemo(() => ({
    field: (i: number, key: string, val: any, t: string) => { setDocData(d => PBModel.applyEdit(d, `items.${i}.${key}`, t === 'num' ? 'num' : 'text', val)); },
    add: () => { setDocData(d => PBModel.addItem(d)); },
    remove: (i: number) => { setDocData(d => PBModel.removeItem(d, i)); },
    move: (i: number, dir: number) => { setDocData(d => PBModel.moveItem(d, i, dir)); },
  }), []);

  const setFlag = (k: string, v: any) => { setFlags(f => ({ ...f, [k]: v })); };
  const setFooterNote = (v: string) => setBrand(b => ({ ...b, footerNote: v }));

  const onTemplateEdit = useCallback((k: number, text: string) => {
    const unit = unitsRef.current[k]; if (!unit) return;
    const next = PBTpl.applyTemplateEdit(effTemplate, unit, text);
    if (next === effTemplate) return;
    if (isInvoice) setCustomTpl(next); else setTemplate(next);
  }, [effTemplate, isInvoice]);

  const onTokenClick = useCallback((k: number, rect: any) => {
    const u = unitsRef.current[k]; if (!u) return;
    setPicker({ k, rect, current: u.helper ? u.helper + ' ' + u.path : u.path });
  }, []);

  const onPickField = useCallback((tag: string) => {
    setPicker((p: any) => {
      if (p) {
        const u = unitsRef.current[p.k];
        const next = u && PBTpl.applyTemplateToken(effTemplate, u, tag);
        if (next && next !== effTemplate) { if (isInvoice) setCustomTpl(next); else setTemplate(next); }
      }
      return null;
    });
  }, [effTemplate, isInvoice]);

  const api = useMemo(() => ({
    setTemplate: (str: string) => { setTemplate(str); },
    patchStyle: (patch: any) => setStyle(s => ({ ...s, ...patch })),
    restore: (before: any) => { setTemplate(before.template); setStyle(before.style); },
    switchDoc: (id: string) => setDocId(id),
    toast,
  }), []);

  const resetAll = () => {
    setTemplate(PBTemplates[docId]); setDocData(PBModel.recomputeTotals(PBModel.clone(doc.sample)));
    setCols(PBTpl.defaultColumns()); setMetaFields(PBTpl.defaultMeta()); setCustomTpl(null);
    setStyle(defStyle(docId)); setFrame(defFrame()); setFlags(defFlags(docId)); setName(doc.full); setStationerySel(stDefault);
    toast('Reset to default');
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const exportPDF = () => { toast('Preparing print-ready PDF…'); setTimeout(() => window.print(), 350); };

  const goHome = () => { navigate(-1); };
  const goOpen = (id: string) => { setDocId(id); };
  const goStationery = () => { setViewMode('stationery'); };

  const startResize = (e: any) => {
    e.preventDefault();
    const startX = e.clientX, startW = dockW;
    const move = (ev: any) => setDockW(Math.max(320, Math.min(960, startW + (startX - ev.clientX))));
    const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); document.body.style.cursor = ''; };
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up); document.body.style.cursor = 'col-resize';
  };

  const handleDragStart = (e: DragStartEvent) => {
    setActiveDragType(e.active.data.current?.type || null);
    setActiveDragData(e.active.data.current || null);
    setActiveDragId(e.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    if (!over) {
      setOverSection(null);
      return;
    }
    if (over.id === 'main-canvas') {
      setActiveCanvas('main');
    } else {
      setOverSection(over.id as string);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragType(null);
    setActiveDragData(null);
    setActiveDragId(null);
    setOverSection(null);
    const { active, over } = event;
    if (!over) return;

    const isFromSidebar =
      active.data.current?.label ||
      active.data.current?.imageUrl ||
      (typeof active.id === 'string' && active.id.startsWith('sidebar-'));

    if (isFromSidebar && active.data.current?.type) {
      const type = active.data.current.type as ElementType;
      const sizes = DEFAULT_ELEMENT_SIZES[type] || { width: 200, height: 80 };

      // Compute drop coordinates relative to the A4 canvas
      const canvasEl = document.querySelector('[data-free-canvas="true"]') as HTMLElement | null;
      let dropX = 52;
      let dropY = 52;

      if (canvasEl) {
        const canvasRect = canvasEl.getBoundingClientRect();
        const translated = event.active.rect.current.translated;
        // Use the translated drop position if available, otherwise center
        const pointerX = translated ? translated.left + translated.width / 2 : (canvasRect.left + canvasRect.width / 2);
        const pointerY = translated ? translated.top + translated.height / 2 : (canvasRect.top + canvasRect.height / 2);
        dropX = Math.max(0, Math.min(canvasRect.width - sizes.width, pointerX - canvasRect.left - sizes.width / 2));
        dropY = Math.max(0, Math.min(canvasRect.height - sizes.height, pointerY - canvasRect.top - sizes.height / 2));
      }

      const maxZ = sections.length > 0 ? Math.max(...sections.map(s => s.zIndex ?? 1)) : 0;

      const initialData = getInitialSectionData(type, {
        src: active.data.current.imageUrl,
        alt: active.data.current.label
      });

      const newSection: EnhancedSectionData = {
        ...initialData,
        id: `sec-${Date.now()}`,
        x: dropX,
        y: dropY,
        width: sizes.width,
        height: sizes.height,
        rotation: 0,
        zIndex: maxZ + 1,
        attributes: {
          ...(initialData.attributes || {}),
          styles: {
            ...(initialData.attributes?.styles || {}),
            width: sizes.width,
            ...(type === 'table' ? {} : { height: sizes.height }),
          }
        }
      };

      // Add to current page
      setPages(prev => {
        const next = [...prev];
        const idx = currentPageIndex;
        if (!next[idx]) return prev;
        next[idx] = { ...next[idx], elements: [...next[idx].elements, newSection] };
        return next;
      });
      setActiveSectionId(newSection.id);
    }
  };

  // ─── Free-positioning handlers ────────────────────────────────────────────────
  const handleMoveElement = useCallback((id: string, x: number, y: number) => {
    setSections(prev => prev.map(s => s.id === id ? { ...s, x, y } : s));
  }, [setSections]);

  const handleResizeElement = useCallback((id: string, x: number, y: number, width: number, height: number) => {
    const rWidth = Math.round(width);
    const rHeight = Math.round(height);
    const rX = Math.round(x);
    const rY = Math.round(y);
    setSections(prev => prev.map(s => s.id === id ? {
      ...s,
      x: rX,
      y: rY,
      width: rWidth,
      height: rHeight,
      attributes: {
        ...(s.attributes || {}),
        styles: {
          ...(s.attributes?.styles || {}),
          width: rWidth,
          ...(s.type === 'table' ? {} : { height: rHeight }),
        }
      }
    } : s));
  }, [setSections]);

  const handleRotateElement = useCallback((id: string, rotation: number) => {
    setSections(prev => prev.map(s => s.id === id ? { ...s, rotation } : s));
  }, [setSections]);

  const handleBringForward = useCallback((id: string) => {
    setSections(prev => {
      const el = prev.find(s => s.id === id);
      if (!el) return prev;
      const maxZ = Math.max(...prev.map(s => s.zIndex ?? 1));
      return prev.map(s => s.id === id ? { ...s, zIndex: maxZ + 1 } : s);
    });
  }, [setSections]);

  const handleSendBackward = useCallback((id: string) => {
    setSections(prev => {
      const el = prev.find(s => s.id === id);
      if (!el) return prev;
      const minZ = Math.min(...prev.map(s => s.zIndex ?? 1));
      return prev.map(s => s.id === id ? { ...s, zIndex: Math.max(0, minZ - 1) } : s);
    });
  }, [setSections]);

  const handleUpdateSection = useCallback((id: string, content: string, attrs: any) => {
    setSections(prev => prev.map(s => s.id === id ? { ...s, content, ...attrs } : s));
  }, [setSections]);

  const handleMoveElementToPage = useCallback((id: string, offset: -1 | 1) => {
    setPages(prev => {
      let elem: EnhancedSectionData | null = null;
      let fromIdx = -1;

      for (let i = 0; i < prev.length; i++) {
        const found = prev[i].elements.find(s => s.id === id);
        if (found) {
          elem = found;
          fromIdx = i;
          break;
        }
      }

      if (!elem || fromIdx === -1) return prev;

      const toIdx = fromIdx + offset;
      if (toIdx < 0 || toIdx >= prev.length) return prev;

      const next = [...prev];
      next[fromIdx] = { ...next[fromIdx], elements: next[fromIdx].elements.filter(s => s.id !== id) };
      next[toIdx] = { ...next[toIdx], elements: [...next[toIdx].elements, elem] };

      setCurrentPageIndex(toIdx);
      return next;
    });
  }, []);

  if (viewMode === 'stationery') {
    return <StationeryMasterApp
      onBack={(updatedLib, activeId) => {
        if (updatedLib) setStLib(updatedLib);
        if (activeId) setStationerySel(activeId);
        setViewMode('template');
      }}
      company_id={locationState?.company_id || selectedCompany || apiPayload?.company_id}
      initialPresetId={stationerySel}
      isNewTemplate={!template_id}
      initialPaper={paper}
      initialOrient={orient}
      placeholders={(() => {
        let dynamicMetaFields: any[] = [];
        if (dynamicGroups && dynamicGroups.length > 0) {
          dynamicGroups.forEach((g: any) => {
            if (g.key !== arrayKey) {
              g.fields.forEach((f: any) => {
                const fieldId = f.tag.replace('{{', '').replace('}}', '');
                dynamicMetaFields.push({ id: fieldId, value: fieldId, label: f.label, expr: f.tag, type: 'text' });
              });
            }
          });
        }
        return dynamicMetaFields.length > 0 ? dynamicMetaFields : [];
      })()}
    />;
  }

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} collisionDetection={pointerWithin}>
      <div className="pb">
        <div className="pb-bar">
          <button className="pb-bar-back" onClick={goHome}><Ic k="arrowL" />Templates</button>
          <span className="pb-bar-div" />
          <span className={`pb-bar-doc-ic ${doc.accent}`}><Ic k={doc.icon} /></span>
          <input className="pb-bar-name" value={name} onChange={e => setName(e.target.value)} />
          {template_id && (
            <VariantPicker subjectId={id} templateId={template_id} companyId={selectedCompany || locationState?.company_id || apiPayload?.company_id} currentName={name} docId={docId} onNavigate={(newId: string) => { if (template_id) navigate(window.location.pathname.replace(template_id, newId) + window.location.search); }} />
          )}
          <span className={`pb-bar-stat ${dirty ? 'is-dirty' : 'is-saved'}`} title={dirty ? 'Unsaved changes to the master' : 'All changes saved to the master'}>
            <Ic k={dirty ? 'dot' : 'check'} />{dirty ? 'Unsaved' : 'Saved'}
          </span>
          {dirty && (
            <>
              <button className="pbtn pbtn--primary pbtn--sm" onClick={saveMaster} title="Commit changes to the master (⌘S)"><Ic k="save" />Save master</button>
              <button className="pbtn pbtn--ghost pbtn--sm" onClick={discardChanges} title="Revert to the last saved master"><Ic k="undo" />Discard</button>
            </>
          )}
          <div className="pb-bar-sp" />
          <button className="pbtn pbtn--ghost pbtn--sm" onClick={goStationery}><Ic k="palette" />Edit Header &amp; Footer</button>

          <StationeryPicker lib={stLib} value={stationerySel} onChange={(v: string) => { setStationerySel(v); }} onManage={goStationery} />
          <span className="pb-bar-div" />
          <button className={`pbtn pbtn--sm ${sideOpen ? 'is-on' : ''}`} onClick={() => setSideOpen(v => !v)}><Ic k="sliders" />Design</button>
          <button className={`pbtn pbtn--sm ${dock === 'html' ? 'is-on' : ''}`} onClick={() => setDock(d => d === 'html' ? 'none' : 'html')}><Ic k="code" />HTML</button>
          <button className="pbtn pbtn--ghost pbtn--sm" onClick={resetAll} title="Reset to default"><Ic k="undo" /></button>
        </div>

        <div className="pb-work">
          {sideOpen && (
            <div className="pb-side">
              <div className="pb-side-head">
                <span className="pb-side-h-t">Design</span>
                <span className="pb-side-h-s">{doc.full}</span>
                <button className="pb-side-collapse" onClick={() => setSideOpen(false)} title="Collapse panel"><Ic k="chevL" /></button>
              </div>
              <div className="pb-side-scroll">
                <ControlsPanel doc={doc} data={data} style={style} setStyle={setStyle} frame={frame} setFrame={setFrame}
                  flags={flags} setFlag={setFlag} paper={paper} setPaper={setPaper} orient={orient} setOrient={setOrient}
                  itemOps={itemOps} setFooterNote={setFooterNote}
                  stnKind={stSel.kind} stnName={stSel.kind === 'preset' ? stSel.preset?.name : (stSel.kind === 'preprinted' ? 'Pre-printed paper' : 'Blank')} onManageStationery={goStationery}
                  cols={isInvoice ? cols : undefined} setCols={setCols} metaFields={metaFields} setMetaFields={setMetaFields} isCustom={isCustom}
                  dynamicGroups={dynamicGroups} arrayKey={arrayKey}
                  activeSectionId={activeSectionId} sections={sections} setSections={setSections}
                  onRevertCustom={() => { setCustomTpl(null); toast('Reverted to visual builder'); }} />
              </div>
            </div>
          )}

          <div className="pb-stage">
            <style>{`
              .pb-stage .st {
                --pbd-accent: ${style?.accent || '#1B2C6E'} !important;
                ${stSel.kind === 'preset' && stSel.preset?.brand ? `--st-brand: ${stSel.preset.brand} !important;` : ''}
              }
              .pb-stage .pbd-page {
                width: ${dims(paper, orient).w}px !important;
                min-width: ${dims(paper, orient).w}px !important;
                max-width: ${dims(paper, orient).w}px !important;
                min-height: ${dims(paper, orient).h}px !important;
              }
            `}</style>
            <div className="pb-lensbar">
              <div className="pb-lens">
                <button className={lens === 'placeholder' ? 'is-active' : ''} onClick={() => setLens('placeholder')}><Ic k="type" />Edit</button>
                <button className={lens === 'filled' ? 'is-active' : ''} onClick={() => setLens('filled')}><Ic k="eye" />Preview</button>
              </div>
            </div>
            <div className="pb-stage-scroll" ref={scrollRef}>
              <div className="pb-zoomwrap" style={{ transform: `scale(${z})`, transformOrigin: 'top center', position: 'relative' }}>

                <EditorCanvas
                  sections={sections}
                  activeId={activeDragId}
                  selectedSection={activeSectionId}
                  onSelectSection={setActiveSectionId}
                  onUpdateSection={(id, data) => handleUpdateSection(id, data.content, data)}
                  onDeleteSection={(id) => {
                    setSections(prev => prev.filter(s => s.id !== id));
                    if (activeSectionId === id) setActiveSectionId(null);
                  }}
                  onMoveElement={handleMoveElement}
                  onResizeElement={handleResizeElement}
                  onRotateElement={handleRotateElement}
                  onBringForward={handleBringForward}
                  onSendBackward={handleSendBackward}
                  preview={lens === 'filled'}
                  editHtml={false}
                  onApplyHtmlEdit={() => { }}
                  onCancelHtmlEdit={() => { }}
                  zoom={100}
                  placeholders={[]}
                  stConfig={stSel}
                  stationeryHeaderHtml={stationeryHeaderHtml}
                  stationeryFooterHtml={stationeryFooterHtml}
                  paper={paper}
                  orient={orient}
                  watermark={style?.watermark}
                  // Multi-page props
                  pages={pages}
                  currentPageIndex={currentPageIndex}
                  onSetCurrentPage={setCurrentPageIndex}
                  onAddPage={addPage}
                  onDeletePage={deletePage}
                  onMoveElementToPage={handleMoveElementToPage}
                  onUpdatePageSections={(pageIdx, updatedSections) => {
                    setPages(prev => {
                      const next = [...prev];
                      if (!next[pageIdx]) return prev;
                      next[pageIdx] = { ...next[pageIdx], elements: updatedSections };
                      return next;
                    });
                  }}
                />
              </div>
            </div>
            <div className="pb-zoomfloat">
              <button onClick={() => setZoom(Math.max(0.3, (typeof z === 'number' ? z : 1) - 0.1))}><Ic k="minus" /></button>
              <span className="pb-zoom-val">{Math.round((typeof z === 'number' ? z : 1) * 100)}%</span>
              <button onClick={() => setZoom(Math.min(1.6, (typeof z === 'number' ? z : 1) + 0.1))}><Ic k="plus" /></button>
              <button onClick={() => setZoom('fit')} title="Fit width"><Ic k="fit" /></button>
              <span className="pb-zoom-div" />
              {/* Page navigation */}
              <button
                className="pbtn pbtn--ghost pbtn--sm"
                disabled={currentPageIndex === 0}
                onClick={() => setCurrentPageIndex(i => Math.max(0, i - 1))}
                title="Previous page"
              ><Ic k="arrowL" /></button>
              <span className="pb-zoom-pages" style={{ minWidth: 90, textAlign: 'center' }}>
                Page {currentPageIndex + 1} / {pages.length} · {paper}{orient === 'landscape' ? ' (L)' : ''}
              </span>
              <button
                className="pbtn pbtn--ghost pbtn--sm"
                disabled={currentPageIndex >= pages.length - 1}
                onClick={() => setCurrentPageIndex(i => Math.min(pages.length - 1, i + 1))}
                title="Next page"
              ><Ic k="arrowR" /></button>
              <button
                className="pb-zf-text-btn"
                onClick={addPage}
                title="Add a new page"
                style={{ marginLeft: 4 }}
              ><Ic k="plus" /> Add Page</button>
              {pages.length > 1 && (
                <button
                  className="pbtn pbtn--ghost pbtn--sm"
                  onClick={() => deletePage(currentPageIndex)}
                  title="Delete current page"
                  style={{ color: '#ef4444' }}
                ><Ic k="trash" /></button>
              )}
            </div>
          </div>

          {dock !== 'none' && (
            <div className="pb-dock" style={{ width: dock === 'html' ? dockW : 340 }}>
              <div className="pb-dock-resizer" onMouseDown={startResize} title="Drag to resize" />
              {dock === 'html' && (
                <div className="pb-htmltab">
                  <div className="pb-rail">
                    <div className="pb-rail-tabs">
                      <button className={`pb-rail-tab ${railTab === 'fields' ? 'is-active' : ''}`} onClick={() => setRailTab('fields')}><Ic k="list" />Fields</button>
                      <button className={`pb-rail-tab ${railTab === 'docs' ? 'is-active' : ''}`} onClick={() => setRailTab('docs')}><Ic k="file" />Docs</button>
                    </div>
                    <div className="pb-rail-body">
                      {railTab === 'fields'
                        ? <FieldTree groups={dynamicGroups && dynamicGroups.length > 0 ? dynamicGroups : doc.groups} onInsert={(tag: string) => { (editorApi.current.insert || (() => { }))(tag); toast('Inserted ' + tag); }} />
                        : <div className="pb-tlist">{order.map(id => {
                          const d = DOCS[id]; return (
                            <button key={id} className={`pb-titem ${id === docId ? 'is-active' : ''}`} onClick={() => goOpen(id)}>
                              <span className={`pb-titem-ic ${d.accent}`}><Ic k={d.icon} /></span>
                              <span><span className="pb-titem-t" style={{ display: 'block' }}>{d.name}</span><span className="pb-titem-s">{d.full}</span></span>
                            </button>);
                        })}</div>}
                    </div>
                  </div>
                  <div className="pb-editor">
                    <div className="pb-editor-bar">
                      <span className="pb-editor-bar-t"><Ic k="code" />{isCustom ? 'Custom HTML · overrides the builder' : (isInvoice ? 'Template source · ships to server' : 'Template · HTML + {{merge}}')}</span>
                      <div style={{ flex: 1 }} />
                      {isInvoice && !isCustom && <button className="pbtn pbtn--ghost pbtn--sm" onClick={() => { setCustomTpl(effTemplate); toast('Editing raw HTML'); }}><Ic k="code" />Edit as HTML</button>}
                      {isCustom && <button className="pbtn pbtn--ghost pbtn--sm" onClick={() => { setCustomTpl(null); toast('Reverted to visual builder'); }}><Ic k="undo" />Revert to builder</button>}
                      <button className="pb-dock-close" onClick={() => setDock('none')}><Ic k="x" /></button>
                    </div>
                    <CodeEditor value={effTemplate} onChange={(s: string) => { if (isInvoice) setCustomTpl(s); else setTemplate(s); }} apiRef={editorApi} dirty={dirty} readOnly={isInvoice && !isCustom} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {picker && (
          <PBPicker.FieldPicker catalog={fieldCatalog} rect={picker.rect} current={picker.current}
            onPick={onPickField} onClose={() => setPicker(null)} />
        )}
        {isModalOpen && apiPayload && (
          <TemplateNameModal
            isOpen={isModalOpen}
            setIsOpen={() => { setIsModalOpen(false); setApiPayload(null); }}
            payload={apiPayload}
            templateRoutes={{ LIST: modulePath }}
            mode={template_id ? 'edit' : 'add'}
            templateData={{ name }}
            onSaveSuccess={handleSaveSuccess}
            onSaveError={handleSaveError}
            isSaving={false}
          />
        )}

        {toastMsg && <div className="pb-toast"><Ic k="check" />{toastMsg}</div>}

        {activeDragId && (
          <DragOverlay dropAnimation={null}>
            {activeDragType === 'image' && activeDragData ? (
              <div style={{ width: 120, height: 120, border: '2px solid #4AC08C', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                <img src={activeDragData.imageUrl} alt={activeDragData.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ) : activeDragData ? (
              <div style={{ padding: '12px 24px', background: '#4AC08C', color: '#fff', borderRadius: 8, fontWeight: 500, boxShadow: '0 4px 12px rgba(74, 192, 140, 0.3)' }}>
                {activeDragData.label || 'Dropping section...'}
              </div>
            ) : activeCanvasSection ? (
              <div style={{ padding: '12px 24px', background: '#fff', border: '1px solid #ddd', borderRadius: 4, opacity: 0.8 }}>
                Moving section...
              </div>
            ) : null}
          </DragOverlay>
        )}
      </div>
    </DndContext>
  );
};

export default SectionFlowEditor;
